<template>
  <h1>Historial de solicitudes</h1>
  <p class="mt-2 text-800 font-medium">
    Lista de las solicitudes en las que participas como aprobador.
  </p>
  <br />
  <Divider></Divider>
  <DataTable
    ref="dt"
    :value="employeeRequests"
    dataKey="id"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    paginator
    :rows="10"
    v-model:filters="filters"
    filter-display="row"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    :globalFilterFields="['employee', 'type']"
    @filter="onFilter($event)"
  >
    <template #header>
      <div class="flex justify-content-end">
        <Button
          icon="pi pi-file-excel"
          label="Exportar"
          @click="exportCSV()"
          class="text-sm font-light bg-green-100 border-green-100 text-green-700 mr-3"
        />
        <InputText v-model="filters['global'].value" placeholder="Buscar" />
      </div>
      <div class="flex justify-content-end"></div>
    </template>
    <Column
      field="employee"
      header="Empleado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="employees"
          optionLabel=""
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
          filter
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      header="Tipo de solicitud"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      field="type"
      filter
    >
      <template #body="{ data, field }">
        {{ formatRequestType(data[field]) }}
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="types"
          optionLabel=""
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
        >
          <template #option="slotProps">
            <span>{{ formatRequestType(slotProps.option) }}</span>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column header="Cantidad" field="quantity">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Fecha de inicio" field="dateInit">
      <template #body="{ data, field }">
        <span v-basic-date="data[field]" v-if="data[field]"></span>
      </template>
    </Column>
    <Column header="Fecha final" field="dateEnd">
      <template #body="{ data, field }">
        <span v-basic-date="data[field]" v-if="data[field]"></span>
      </template>
    </Column>
    <Column header="Aprobadores" field="aprobaciones">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column
      field="globalStatus"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '5rem' }"
      style="width: 20%"
      filter
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
      <template #filter="{ filterModel, filterCallback, field }">
        <Dropdown
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="statuses"
          placeholder="filtrar estado"
          class="p-column-filter flex align-items-center"
          style="min-width: 12rem; height: 3rem"
          :showClear="true"
        >
          <template #value="{ value }">
            <span class="text-sm text-600 font-medium">
              {{ value && formatRequestState(value) }}
            </span>
          </template>
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
            />
          </template>
        </Dropdown>
      </template>
    </Column>
    <Column
      field="createdAt"
      header="Fecha de creacion"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '5rem' }"
      style="width: 20%"
      filter
    >
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
      <template #filter="{ filterModel }">
        <Button
          icon="pi pi-filter"
          @click="createdAtFilterToggle"
          class="text-light h-2rem"
          v-tooltip.bottom="'filtrar'"
          severity="secondary"
        />
        <OverlayPanel id="overlay" ref="createdAtOp" class="mr-0 pr-0">
          <div class="flex flex-column">
            <Dropdown
              v-model="createdAtFilter.matchMode"
              :options="DatefilterOptions"
              option-label="label"
              option-value="value"
              :show-clear="true"
              class="w-9 text-sm mb-2"
            ></Dropdown>
            <Calendar
              v-model="createdAtFilter.value"
              dateFormat="mm/dd/yy"
              placeholder="mm/dd/yyyy"
              mask="99/99/9999"
              style="min-width: 12rem; height: 3rem"
              :showClear="true"
              @update:model-value="onFilterCreatedAt"
              class="w-9 text-sm"
              showButtonBar
              locale="es"
            />
          </div>
        </OverlayPanel>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <Button
          v-tooltip.bottom="'Ver detalles'"
          icon="pi pi-eye"
          class="h-2rem hover:text-blue-800"
          severity="primary"
          @click="goToDetail(data['id'])"
        />
      </template>
    </Column>
  </DataTable>
</template>
<script setup lang="ts">
  import Tag from 'primevue/tag'
  import { useUserStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import { onMounted, reactive, ref, watch } from 'vue'
  import { getRequestByApproverId } from '../services/ApprovalRequestService'
  import { ApprovalRequest } from '../models/ApprovalRequest'
  import { formatRequestType } from '@/shared/utils/formatRequestType'
  import { FilterMatchMode, FilterMatchModeOptions } from 'primevue/api'
  import router from '@/router'
  import DataTable from 'primevue/datatable'
  import dayjs from 'dayjs'
  import useLoading from '@/store/load'
  import formatRequestState from '@/shared/utils/formatRequestState'
  import { exportExcel } from '@/shared/utils/reports/requestsReportsXlsx'

  const statuses = ref(['PENDING', 'APPROVED', 'REJECTED'])
  const { user } = storeToRefs(useUserStore())
  const employeeRequests = ref<ApprovalRequest[]>()
  const employeeRequestsPivot = ref<ApprovalRequest[]>()
  const employees = ref()
  const types = ref()
  const dt = ref<any>()
  const filteredRequests = ref<ApprovalRequest[]>()
  const loadingStore = useLoading()

  //start filter
  interface Filter {
    value: any
    matchMode: string
  }

  const DatefilterOptions = [
    { label: 'Igual a', value: FilterMatchMode.DATE_IS },
    { label: 'Antes de', value: FilterMatchMode.DATE_BEFORE },
    { label: 'Despues de', value: FilterMatchMode.DATE_AFTER },
  ]

  const createdAtOp = ref()

  const createdAtFilterToggle = (event: any) => {
    createdAtOp.value.toggle(event)
  }

  const createdAtFilter = reactive<Filter>({
    value: null,
    matchMode: FilterMatchMode.DATE_IS,
  })

  watch(createdAtFilter, async (value) => {
    if (!value.value && !value.matchMode) {
      employeeRequests.value = employeeRequestsPivot.value
    }

    if (!value.matchMode && value.value) {
      value.value = null
      employeeRequests.value = employeeRequestsPivot.value
    }

    if (value.matchMode && value.value) {
      onFilterCreatedAt(value.value)
    }
  })

  const onFilterCreatedAt = (value: any) => {
    createdAtFilter.value = value
    employeeRequests.value = employeeRequestsPivot.value
    if (createdAtFilter.matchMode) {
      const filteredValues =
        employeeRequests.value?.filter((request: ApprovalRequest) => {
          const date = request.createdAt
          switch (createdAtFilter.matchMode) {
            case 'dateIs':
              return dayjs(date, 'mm/dd/yy').isSame(
                createdAtFilter.value,
                'day'
              )
            case 'dateBefore':
              return dayjs(date, 'mm/dd/yy').isBefore(
                createdAtFilter.value,
                'day'
              )
            case 'dateAfter':
              return dayjs(date, 'mm/dd/yy').isAfter(
                createdAtFilter.value,
                'day'
              )
            default:
              return dayjs(date, 'mm/dd/yy').isSame(
                createdAtFilter.value,
                'day'
              )
          }
        }) || []

      filteredRequests.value =
        filteredValues?.length > 0 ? filteredValues : employeeRequests.value
      employeeRequests.value = filteredValues
    }
  }
  //end filter

  onMounted(async () => {
    await refreshRequests()
  })

  const onFilter = (event: any) => {
    const { filteredValue } = event
    const filteredValueCasted = filteredValue as ApprovalRequest[]
    filteredRequests.value =
      filteredValueCasted.length > 0
        ? filteredValueCasted
        : employeeRequests.value
  }

  const exportCSV = () => {
    if (!filteredRequests.value) return
    exportExcel(filteredRequests.value)
  }

  const refreshRequests = async () => {
    loadingStore.setLoadingState()
    const requestResponse = await getRequestByApproverId(
      user.value?.approverId || ''
    )
    employeeRequests.value = requestResponse
    filteredRequests.value = requestResponse
    employeeRequestsPivot.value = requestResponse
    employees.value = requestResponse.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e === curr.employee)) {
        acc.push(curr.employee)
      }
      return acc
    }, [])
    types.value = requestResponse.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e === curr.type)) {
        acc.push(curr.type)
      }
      return acc
    }, [])
    loadingStore.dismissLoadingState()
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      default:
        return 'warning'
    }
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    globalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    employee: { value: null, matchMode: FilterMatchMode.IN },
    type: { value: null, matchMode: FilterMatchMode.IN },
    createdAt: { value: null, matchMode: FilterMatchMode.DATE_IS },
  })

  const goToDetail = (id: string) => {
    router.push(`/requestDetail/${id}/from/history`)
  }
</script>
<style scoped>
  tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }
</style>
